// Shared saved-list name/error contract for the SavedLists modals (SaveToListModal, CreateListModal,
// EditListNameModal). All hit the same UT_API saved-list endpoints, so the name cap, 400-shape
// detection, and inline-message resolution live here as a single source of truth rather than being
// copied per modal. Surface-specific copy (titles, the generic fallback line) stays in each modal;
// only the neutral constants + detection/resolution logic are shared.

export const MAX_LIST_NAME_LENGTH = 50;
export const HTTP_BAD_REQUEST = 400;
export const DUPLICATE_LIST_NAME_MATCH_PHRASE = 'already exists';
export const GENERIC_ERROR_TOAST_TITLE = 'Something Went Wrong';
// Distinct name from savedListsSave.js's DUPLICATE_LIST_NAME_ERROR_MESSAGE (different, longer copy) so
// the two same-folder surfaces can't be cross-imported by mistake.
export const DUPLICATE_LIST_NAME_INLINE_MESSAGE = 'A list with this name already exists.';

// UT_API's real 400 shape for the saved-list endpoints is { StatusCode, DisplayMessage } (PascalCase,
// .NET convention) with no distinct machine error code — confirmed against the live API. Matches on
// the human-readable message rather than stringifying the whole payload, so a differently-shaped 400
// that just happens to echo unrelated text elsewhere in the body isn't misread as a duplicate-name
// error. Trimmed to the confirmed field plus one camelCase fallback rather than guessing at further
// casings/shapes that have never been observed from the live API.
export const isDuplicateListNameError = (error) => {
    if (error?.response?.status !== HTTP_BAD_REQUEST) {
        return false;
    }
    const message = error.response.data?.DisplayMessage || error.response.data?.message;
    return typeof message === 'string' && message.toLowerCase().includes(DUPLICATE_LIST_NAME_MATCH_PHRASE);
};

export const isBadRequest = (error) => error?.response?.status === HTTP_BAD_REQUEST;

// Maps a create/rename 400 to the inline message shown under the name input: a duplicate collision
// gets the design's fixed copy, any other 400 echoes UT_API's human-readable DisplayMessage verbatim,
// and a shapeless 400 falls back to the surface-specific generic line the caller supplies.
export const resolveInlineListNameError = (error, genericMessage) => {
    if (isDuplicateListNameError(error)) {
        return DUPLICATE_LIST_NAME_INLINE_MESSAGE;
    }
    const message = error?.response?.data?.DisplayMessage || error?.response?.data?.message;
    if (typeof message === 'string' && message.trim().length > 0) {
        return message;
    }
    return genericMessage;
};
